import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { changeLanguage } from '../../i18n';
import { Container, PillLink, Reveal } from './primitives';

const PRODUCT_LINKS = ['platform', 'clients', 'projects', 'finance', 'portal'];
const ACCOUNT_LINKS = [
  { id: 'signIn', to: '/login' },
  { id: 'createAccount', to: '/register' },
  { id: 'resetPassword', to: '/forgot-password' }
];
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية' }
];

const footerLink = 'lp-focus inline-block rounded py-1 text-[15px] text-white/60 transition-colors hover:text-white';

export default function ClosingSection() {
  const { t, i18n } = useTranslation();

  return (
    <footer className="bg-ink text-white" aria-labelledby="closing-title">
      <Container className="max-w-[1194px] py-[72px] lg:py-24">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal className="text-center lg:text-start">
            <h2 id="closing-title" className="lp-h2 mx-auto max-w-[520px] text-white lg:mx-0">
              {t('landing.closing.title')}
            </h2>
            <p className="mt-6 text-sm text-white/60">{t('landing.closing.note')}</p>
          </Reveal>

          <Reveal delay={120} className="flex flex-col gap-3">
            <div className="lp-canvas lp-canvas-cta flex h-[200px] items-center justify-center lg:h-[180px]">
              <PillLink to="/register" variant="light">
                {t('landing.cta.getStarted')}
              </PillLink>
            </div>
            <div className="rounded-md bg-ink-soft p-7 sm:p-8">
              <p className="text-[17px] font-medium leading-[1.4]">{t('landing.closing.signInTitle')}</p>
              <p className="mt-2 text-[17px] leading-[1.4] text-white/60">{t('landing.closing.signInText')}</p>
              <Link
                to="/login"
                className="lp-focus mt-6 inline-flex h-10 items-center rounded-full border border-white/20 px-[18px] text-sm font-medium text-white transition-colors hover:border-white/40 hover:bg-white/5 focus-visible:ring-offset-ink-soft"
              >
                {t('landing.nav.signIn')}
              </Link>
            </div>
          </Reveal>
        </div>

        <div className="mt-20 grid gap-10 border-t border-white/10 pt-12 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <Link to="/" className="lp-focus -m-1 inline-block rounded-md p-1 focus-visible:ring-offset-ink" aria-label={t('landing.nav.home')}>
              <img src="/proctm_logo_blue.png" alt="ProCTM" width="500" height="300" className="-mx-2 h-14 w-auto brightness-0 invert" loading="lazy" />
            </Link>
            <p className="mt-4 max-w-[300px] text-[15px] leading-[1.5] text-white/60">{t('landing.footer.description')}</p>
          </div>

          <nav aria-labelledby="footer-product">
            <h3 id="footer-product" className="text-sm font-medium">{t('landing.footer.product')}</h3>
            <ul className="mt-3 space-y-1">
              {PRODUCT_LINKS.map((id) => (
                <li key={id}>
                  <a href={`#${id}`} className={footerLink}>
                    {t(`landing.footer.links.${id}`)}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-labelledby="footer-account">
            <h3 id="footer-account" className="text-sm font-medium">{t('landing.footer.account')}</h3>
            <ul className="mt-3 space-y-1">
              {ACCOUNT_LINKS.map(({ id, to }) => (
                <li key={id}>
                  <Link to={to} className={footerLink}>
                    {t(`landing.footer.links.${id}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h3 className="text-sm font-medium">{t('landing.footer.language')}</h3>
            <ul className="mt-3 space-y-1">
              {LANGUAGES.map(({ code, label }) => {
                const current = i18n.language === code;
                return (
                  <li key={code}>
                    <button
                      type="button"
                      lang={code}
                      onClick={() => changeLanguage(code)}
                      aria-pressed={current}
                      className={`${footerLink} ${current ? 'text-white' : ''}`}
                    >
                      {label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-white/10 pt-8 text-sm text-white/60 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} ProCTM. {t('landing.footer.rights')}
          </p>
          <p>
            {t('landing.footer.builtBy')}{' '}
            <a
              href="https://josam-portfolio.vercel.app"
              target="_blank"
              rel="noopener noreferrer"
              className="lp-focus rounded text-white/80 underline-offset-2 hover:text-white hover:underline"
            >
              Gerges Samuel
            </a>
          </p>
        </div>
      </Container>
    </footer>
  );
}
